import { useState } from 'react';
import type {
  BlockData,
  BlockSpec,
  Driver,
  Performer,
  RenderedBlock,
  RenderedDashboard,
} from '../api';
import type { VizColors } from '../theme';
import {
  Bars,
  Donut,
  ForecastBand,
  Heatmap,
  Histogram,
  MultiTrend,
  Scatter2,
  formatCompact,
} from './AiCharts';

/**
 * Render an automatically composed dashboard.
 *
 * The component is a switch over block kinds and deliberately holds no state
 * beyond which rationale is open. All the interaction state — filters, search,
 * the cross-filter — lives one level up in the page, because those things
 * change the *data* and the data is fetched in a single request. Pushing that
 * state down here would mean each block fetching its own, which is the design
 * this whole feature exists to avoid.
 *
 * `onCrossFilter` is what makes the charts controls rather than pictures:
 * clicking a bar, a slice or a heatmap cell adds a filter that every other
 * block on the page then re-renders against.
 */
export function AutoDashboard({
  view,
  colors,
  busy,
  crossFilter,
  onCrossFilter,
  onDrillPeriod,
}: {
  view: RenderedDashboard;
  colors: VizColors;
  busy: boolean;
  crossFilter: { column: string; value: string } | null;
  onCrossFilter: (column: string, value: string) => void;
  onDrillPeriod?: (column: string, key: string) => void;
}) {
  return (
    <div className={busy ? 'auto-grid busy' : 'auto-grid'}>
      {view.blocks.map((block) => (
        <Block
          key={block.spec.id}
          block={block}
          colors={colors}
          roles={view.spec.roles}
          crossFilter={crossFilter}
          onCrossFilter={onCrossFilter}
          onDrillPeriod={onDrillPeriod}
        />
      ))}
    </div>
  );
}

function Block({
  block,
  colors,
  roles,
  crossFilter,
  onCrossFilter,
  onDrillPeriod,
}: {
  block: RenderedBlock;
  colors: VizColors;
  roles: RenderedDashboard['spec']['roles'];
  crossFilter: { column: string; value: string } | null;
  onCrossFilter: (column: string, value: string) => void;
  onDrillPeriod?: (column: string, key: string) => void;
}) {
  const [showReason, setShowReason] = useState(false);
  const { spec } = block;

  return (
    <section className={`auto-block width-${spec.width}`} id={`block-${spec.id}`}>
      <header>
        <div className="auto-block-title">
          <h3>{spec.title}</h3>
          <p>{spec.subtitle}</p>
        </div>
        {/*
         * Nobody chose this chart, so it has to be able to say why it is here.
         * Without this the first reaction to an unexpected block is "why am I
         * looking at this", and the second is distrust of the ones beside it.
         */}
        <button
          className="reason-toggle"
          onClick={() => setShowReason(!showReason)}
          aria-expanded={showReason}
          title="Why is this here?"
        >
          {showReason ? '−' : '?'}
        </button>
      </header>

      {showReason && <p className="auto-reason">{spec.rationale}</p>}

      <div className="auto-block-body">
        {block.data === null ? (
          <p className="muted auto-empty">{block.unavailable ?? 'Nothing to show.'}</p>
        ) : (
          <Body
            spec={spec}
            data={block.data}
            colors={colors}
            roles={roles}
            crossFilter={crossFilter}
            onCrossFilter={onCrossFilter}
            onDrillPeriod={onDrillPeriod}
          />
        )}
      </div>
    </section>
  );
}

function Body({
  spec,
  data,
  colors,
  roles,
  crossFilter,
  onCrossFilter,
  onDrillPeriod,
}: {
  spec: BlockSpec;
  data: BlockData;
  colors: VizColors;
  roles: RenderedDashboard['spec']['roles'];
  crossFilter: { column: string; value: string } | null;
  onCrossFilter: (column: string, value: string) => void;
  onDrillPeriod?: (column: string, key: string) => void;
}) {
  const dimension = typeof spec['dimension'] === 'string' ? spec['dimension'] : null;
  const selected = crossFilter !== null && crossFilter.column === dimension ? crossFilter.value : null;

  switch (data.kind) {
    // -- KPIs ---------------------------------------------------------------
    case 'kpi':
      return (
        <div className="kpi-row">
          {data.cards.map((card) => (
            <article className="kpi" key={card.id}>
              <span className="kpi-title">{card.title}</span>
              <span className="kpi-value">{card.formatted}</span>
              <span className="kpi-foot">
                {card.trend === null ? (
                  // No arrow rather than a 0.0% placeholder: a marker that
                  // always renders teaches you to ignore the ones that mean
                  // something.
                  <span className="muted">{card.subtitle}</span>
                ) : (
                  <>
                    <span className={`delta ${card.trend.direction}`}>
                      {card.trend.direction === 'up' ? '▲' : card.trend.direction === 'down' ? '▼' : '■'}{' '}
                      {Math.abs(card.trend.changePercent).toFixed(1)}%
                    </span>
                    <span className="muted"> {card.trend.basis}</span>
                  </>
                )}
              </span>
            </article>
          ))}
        </div>
      );

    // -- Trend --------------------------------------------------------------
    case 'trend': {
      const dateColumn = typeof spec['dateColumn'] === 'string' ? spec['dateColumn'] : null;
      return (
        <>
          {data.change !== null && data.change.percent !== null && (
            <p className="auto-note">
              Across the plotted window this is{' '}
              <strong className={`delta ${data.change.direction}`}>
                {data.change.direction === 'up' ? 'up' : data.change.direction === 'down' ? 'down' : 'flat at'}{' '}
                {Math.abs(data.change.percent).toFixed(1)}%
              </strong>
              .
            </p>
          )}
          <MultiTrend
            series={data.series}
            points={data.points}
            colors={colors}
            onSelect={
              dateColumn !== null && onDrillPeriod !== undefined
                ? (key) => onDrillPeriod(dateColumn, key)
                : undefined
            }
          />
        </>
      );
    }

    // -- Comparison ---------------------------------------------------------
    case 'comparison':
      return (
        <div className="compare-row">
          {data.entries.map((entry) => (
            <article className="compare" key={entry.period}>
              <span className="compare-label">{entry.label}</span>
              <span className={`compare-change delta ${entry.direction}`}>
                {entry.changePercent === null
                  ? '—'
                  : `${entry.changePercent > 0 ? '+' : ''}${entry.changePercent.toFixed(1)}%`}
              </span>
              <span className="compare-values">
                {formatCompact(entry.current)} <span className="muted">vs</span>{' '}
                {formatCompact(entry.previous)}
              </span>
              <span className="muted compare-periods">
                {entry.currentLabel} vs {entry.previousLabel}
              </span>
              {/*
               * A partial current period always looks like a collapse. Saying
               * so is the difference between a useful comparison and a false
               * alarm that gets escalated.
               */}
              {!entry.comparable && (
                <span className="badge warning compare-flag">Current period looks incomplete</span>
              )}
            </article>
          ))}
        </div>
      );

    // -- Breakdown ----------------------------------------------------------
    case 'breakdown':
      return (
        <>
          <Bars
            points={data.points}
            other={data.other}
            measureLabel={roles.primaryMeasure ?? 'Rows'}
            colors={colors}
            orientation={spec['orientation'] === 'horizontal' ? 'horizontal' : 'vertical'}
            selected={selected}
            onSelect={dimension === null ? undefined : (key) => onCrossFilter(dimension, key)}
          />
          {data.other !== null && (
            <p className="auto-note muted">
              {data.totalGroups.toLocaleString()} groups in total; everything past the top{' '}
              {data.points.length} is folded into Other so the bars still sum to the whole.
            </p>
          )}
        </>
      );

    // -- Share --------------------------------------------------------------
    case 'share':
      return (
        <Donut
          points={data.points}
          other={data.other}
          total={data.total}
          totalLabel={data.additive ? 'total' : 'sum of parts'}
          colors={colors}
          onSelect={dimension === null ? undefined : (key) => onCrossFilter(dimension, key)}
        />
      );

    // -- Heatmap ------------------------------------------------------------
    case 'heatmap': {
      const rowField = typeof spec['rowField'] === 'string' ? spec['rowField'] : null;
      return (
        <Heatmap
          rowKeys={data.rowKeys}
          columnKeys={data.columnKeys}
          cells={data.cells}
          min={data.min}
          max={data.max}
          colors={colors}
          onSelect={rowField === null ? undefined : (row) => onCrossFilter(rowField, row)}
        />
      );
    }

    // -- Performers ---------------------------------------------------------
    case 'performers':
      return (
        <div className="performers">
          <PerformerList
            title="Top"
            rows={data.top}
            additive={data.additive}
            onSelect={dimension === null ? undefined : (key) => onCrossFilter(dimension, key)}
          />
          {data.bottom.length > 0 && (
            <PerformerList
              title="Bottom"
              rows={data.bottom}
              additive={data.additive}
              onSelect={dimension === null ? undefined : (key) => onCrossFilter(dimension, key)}
            />
          )}
        </div>
      );

    // -- Drivers ------------------------------------------------------------
    case 'drivers':
      return (
        <div className="drivers">
          <p className="auto-note">
            {data.previousLabel} → {data.currentLabel}:{' '}
            <strong className={`delta ${data.total.delta >= 0 ? 'up' : 'down'}`}>
              {data.total.delta >= 0 ? '+' : ''}
              {formatCompact(data.total.delta)}
            </strong>{' '}
            overall.
            {!data.comparable && (
              <span className="badge warning" style={{ marginLeft: 8 }}>
                Current period incomplete
              </span>
            )}
          </p>

          <div className="driver-columns">
            <DriverList
              title="Pushed it up"
              rows={data.gainers}
              onSelect={dimension === null ? undefined : (key) => onCrossFilter(dimension, key)}
            />
            <DriverList
              title="Pulled it down"
              rows={data.losers}
              onSelect={dimension === null ? undefined : (key) => onCrossFilter(dimension, key)}
            />
          </div>
        </div>
      );

    // -- Distribution -------------------------------------------------------
    case 'distribution':
      return (
        <>
          <Histogram bins={data.bins} median={data.median} mean={data.mean} colors={colors} />
          <p className="auto-note muted">
            Median {formatCompact(data.median)}, mean {formatCompact(data.mean)}, skew{' '}
            {data.skewness.toFixed(2)}
            {data.outlierCount > 0 && `, ${data.outlierCount.toLocaleString()} outliers beyond the Tukey fences`}.
          </p>
        </>
      );

    // -- Correlation --------------------------------------------------------
    case 'correlation':
      return (
        <>
          <Scatter2
            points={data.points}
            xLabel={String(spec['x'] ?? 'x')}
            yLabel={String(spec['y'] ?? 'y')}
            colors={colors}
          />
          <p className="auto-note muted">
            r = {data.r.toFixed(3)} over {data.n.toLocaleString()} paired rows
            {data.sampled && ' (marks sampled for legibility; the statistic uses every pair)'}. These move
            together — which is not the same as one causing the other.
          </p>
        </>
      );

    // -- Pivot --------------------------------------------------------------
    case 'pivot':
      return (
        <div className="table-scroll">
          <table className="grid-table">
            <thead>
              <tr>
                <th>{data.table.rowFields.join(' / ')}</th>
                {data.table.columnKeys.map((key) => (
                  <th key={key} className="numeric">
                    {key}
                  </th>
                ))}
                <th className="numeric">{data.table.totalsAreAdditive ? 'Total' : 'Overall'}</th>
              </tr>
            </thead>
            <tbody>
              {data.table.rows.map((row) => (
                <tr key={row.label}>
                  <td>{row.label}</td>
                  {row.cells.map((cell, index) => (
                    <td key={index} className="numeric">
                      {/* A dash, never a zero. "No rows here" and "rows summing
                          to nothing" are different facts. */}
                      {cell.value === null ? '—' : formatCompact(cell.value)}
                    </td>
                  ))}
                  <td className="numeric strong">
                    {row.total.value === null ? '—' : formatCompact(row.total.value)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {!data.table.totalsAreAdditive && (
            <p className="auto-note muted">
              Margins are labelled “Overall”, not “Total”: the margin of an average is the average of
              every underlying row, not the sum of the cell averages.
            </p>
          )}
        </div>
      );

    // -- Anomalies ----------------------------------------------------------
    case 'anomalies':
      if (data.unavailable !== null) return <p className="muted auto-empty">{data.unavailable}</p>;
      if (data.points.length === 0) {
        return (
          <p className="muted auto-empty">
            Nothing unexpected across {data.checked.toLocaleString()} points — every observation fell
            inside the model's prediction band.
          </p>
        );
      }
      return (
        <ul className="anomaly-list">
          {data.points.map((point) => (
            <li key={point.key}>
              <span className="anomaly-when">{point.key}</span>
              <span className={`delta ${point.direction === 'above' ? 'up' : 'down'}`}>
                {point.direction === 'above' ? '▲' : '▼'} {formatCompact(point.actual)}
              </span>
              <span className="muted">expected {formatCompact(point.expected)}</span>
            </li>
          ))}
        </ul>
      );

    // -- Forecast -----------------------------------------------------------
    case 'forecast':
      if (data.unavailable !== null) return <p className="muted auto-empty">{data.unavailable}</p>;
      return (
        <>
          <ForecastBand history={data.history} points={data.points} colors={colors} />
          <p className="auto-note muted">
            Model: {data.model}
            {data.seasonality.length > 0 && ` · detected ${data.seasonality.join(' and ')} seasonality`}. The
            band covers noise and fit, not the chance the trend breaks.
          </p>
        </>
      );

    // -- Table --------------------------------------------------------------
    case 'table':
      return (
        <div className="table-scroll">
          <table className="grid-table">
            <thead>
              <tr>
                {data.columns.map((column) => (
                  <th key={column}>{column}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.rows.map((row, index) => (
                <tr key={index}>
                  {row.map((cell, cellIndex) => (
                    <td key={cellIndex} className={typeof cell === 'number' ? 'numeric' : undefined}>
                      {cell === null ? <span className="muted">—</span> : String(cell)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
          <p className="auto-note muted">
            Showing {data.rows.length.toLocaleString()} of {data.totalRows.toLocaleString()} matching rows.
          </p>
        </div>
      );
  }
}

function PerformerList({
  title,
  rows,
  additive,
  onSelect,
}: {
  title: string;
  rows: readonly Performer[];
  additive: boolean;
  onSelect?: (key: string) => void;
}) {
  return (
    <div className="performer-list">
      <h4>{title}</h4>
      <ol>
        {rows.map((row) => (
          <li key={row.key}>
            <button
              className="performer-key"
              onClick={() => onSelect?.(row.key)}
              disabled={onSelect === undefined}
              title={onSelect === undefined ? undefined : `Filter to ${row.key}`}
            >
              {row.key}
            </button>
            <span className="performer-value">{formatCompact(row.value)}</span>
            {/* A share of a non-additive total is meaningless, so it is omitted
                rather than approximated. */}
            {additive && row.share !== null && (
              <span className="muted performer-share">{(row.share * 100).toFixed(1)}%</span>
            )}
          </li>
        ))}
      </ol>
    </div>
  );
}

function DriverList({
  title,
  rows,
  onSelect,
}: {
  title: string;
  rows: readonly Driver[];
  onSelect?: (key: string) => void;
}) {
  if (rows.length === 0) {
    return (
      <div className="driver-list">
        <h4>{title}</h4>
        <p className="muted">Nothing moved this way.</p>
      </div>
    );
  }

  return (
    <div className="driver-list">
      <h4>{title}</h4>
      <ul>
        {rows.map((row) => (
          <li key={row.key}>
            <button
              className="performer-key"
              onClick={() => onSelect?.(row.key)}
              disabled={onSelect === undefined}
            >
              {row.key}
            </button>
            <span className={`delta ${row.delta >= 0 ? 'up' : 'down'}`}>
              {row.delta >= 0 ? '+' : ''}
              {formatCompact(row.delta)}
            </span>
            <span className="muted driver-share">
              {/* Share of the *movement*, not of the total. A group that is 5%
                  of revenue can be 80% of the decline, and pointing at its 5%
                  would send you to the wrong place. */}
              {Math.abs(row.contribution * 100).toFixed(0)}% of the move
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
